import * as THREE from 'three';
import { glowTexture } from '../door/door-textures.js';
import { blankFace, bronze, canDraw, glyphFace, type GlyphFace } from './hud-materials.js';
import { boltGeometry, copiesGeometry, iron, mergedGeometry, polygonFrameGeometry, polygonGeometry, rectFrameGeometry } from './hud-parts.js';
import { hudIcons } from '../../presentation/icons.js';
import { RadialMenu, unpickable, type RadialNode } from './radial-menu.js';
import { HERO_ACTIONS } from '../../../shared/game/combat-types.js';

/** The portrait's size in the frame (Baldur's Gate's 7:11), the bronze border round it, and the frame's outer size. */
export const PORTRAIT_WIDTH = 70, PORTRAIT_HEIGHT = 110;
const BORDER = 7, OUTER_WIDTH = PORTRAIT_WIDTH + BORDER * 2, OUTER_HEIGHT = PORTRAIT_HEIGHT + BORDER * 2;
const SELECTED_BRONZE = new THREE.Color(0xa47c46), RESTING_BRONZE = new THREE.Color(0x6a5234);
const HALO = new THREE.Color(0xc98a44), RAGE_HALO = new THREE.Color(0xc0442a);
/** The crystal rails' centre lines, just over the frame's top and under its foot, and the spacing of action and wound crystals along them. */
const RAIL = OUTER_HEIGHT / 2 + 8, ACTION_STEP = 21, WOUND_STEP = 12, SUMMON_STEP = 15, FURY_STEP = 22;
/** How much the lamps light a portrait, and how much it shows its own painted light. */
const PORTRAIT_LIT = .2, PORTRAIT_GLOW = 1.35;

/** A crystal pip: its mesh, its holder's glows and which of them is its own, and how lit it is (0–1). */
interface Pip { mesh: THREE.Mesh; glows: HolderGlows | null; index: number; light: number }
/** A holder's crystal glows: one additive point each, drawn together, each point's colour its glow colour times its strength. */
interface HolderGlows { points: THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial>; colors: Float32Array; color: THREE.Color }
/** A glow's width in the medallion's units. */
const GLOW = 32;

/** The stances a medallion shows: Run or Hide (each ends the other) on the lower notch, Anticipate Danger on the upper. */
export type MedallionStance = 'run' | 'hide' | 'anticipate-danger';
export interface MedallionStances { move: 'run' | 'hide' | null; anticipating: boolean; hidden: boolean }
/** Each stance's glyph, its ink and inner light, and its glow's colour and strength. Hide is smoky and dim until the hero is truly unseen. */
const STANCE_LOOKS: Record<MedallionStance | 'hidden', { icon: string; ink: number; emissive: number; glow: number; strength: number }> = {
  run: { icon: hudIcons.run, ink: 0xe89a4a, emissive: 0x6a3a12, glow: 0xff9a40, strength: .5 },
  hide: { icon: hudIcons.hide, ink: 0x98a6b2, emissive: 0x1c242c, glow: 0x6f8296, strength: .14 },
  hidden: { icon: hudIcons.hide, ink: 0xb8cadc, emissive: 0x3c5068, glow: 0x9ab8d8, strength: .5 },
  'anticipate-danger': { icon: hudIcons.anticipate, ink: 0xe2d4a8, emissive: 0x4a3e1c, glow: 0xf0dca0, strength: .4 },
};
/** A notch's vertex radius, and its two places on the frame's left side (the lower holds Run or Hide). */
const NOTCH = 12, NOTCH_MOVE_Y = -36, NOTCH_GUARD_Y = -8;
/** Stance glyph faces, drawn once per look and shared by every medallion. */
const stanceFaces = new Map<string, Promise<GlyphFace | null>>();
const stanceFace = (look: { icon: string; ink: number }) => {
  const key = `${look.icon}|${look.ink}`;
  let face = stanceFaces.get(key);
  if (!face) { face = glyphFace(look.icon, 0x1c140c, look.ink, true, .14).catch(() => null); stanceFaces.set(key, face); }
  return face;
};
/** A stance notch: its plate, its glyph's material and glow, the look it shows, and how far out of the frame it stands (0–1). */
interface Notch { group: THREE.Group; face: THREE.MeshStandardMaterial; glow: THREE.Sprite | null; y: number; look: MedallionStance | 'hidden' | null; light: number }
/** Faceted, glassy and lit from within by `glow` at `strength`; a faint glow leaves it a dead, smoky stone. */
function crystal(color: number, glow: number, strength: number) {
  return new THREE.MeshStandardMaterial({ color, emissive: glow, emissiveIntensity: strength, roughness: .12, metalness: 0, flatShading: true, transparent: true, opacity: strength > 1 ? .96 : .86 });
}

/** A Fury Die's face value, drawn once per value in bone on a dark ground and shared by every medallion. */
const furyNumbers = new Map<number, THREE.CanvasTexture>();
function furyNumber(value: number): THREE.CanvasTexture | null {
  if (!canDraw()) return null;
  let texture = furyNumbers.get(value);
  if (!texture) {
    const canvas = document.createElement('canvas'); canvas.width = canvas.height = 64;
    const context = canvas.getContext('2d')!;
    context.font = 'bold 44px Georgia, serif'; context.textAlign = 'center'; context.textBaseline = 'middle';
    context.lineWidth = 7; context.strokeStyle = 'rgba(20, 4, 2, .95)'; context.strokeText(String(value), 32, 35);
    context.fillStyle = '#f3dcc0'; context.fillText(String(value), 32, 35);
    texture = new THREE.CanvasTexture(canvas); texture.colorSpace = THREE.SRGBColorSpace; furyNumbers.set(value, texture);
  }
  return texture;
}

/**
 * The blood that rises over a portrait as the hero is hurt, the way Baldur's Gate's did, but as a liquid: its surface rolls in two
 * slow waves that slosh higher after a change, with a lit meniscus, darker depths and a faint drifting shimmer. `level` is the share
 * of the portrait it fills (0 unhurt, 1 at no hit points).
 */
function bloodMaterial() {
  return new THREE.ShaderMaterial({
    transparent: true, depthWrite: false,
    uniforms: { level: { value: 0 }, time: { value: 0 }, slosh: { value: 0 }, dim: { value: 0 } },
    vertexShader: 'varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
    fragmentShader: `
      uniform float level, time, slosh, dim; varying vec2 vUv;
      void main() {
        if (level <= .001) discard;
        float swell = .006 + slosh * .03;
        float surface = level + (sin(vUv.x * 8.0 + time * 2.1) * .6 + sin(vUv.x * 15.0 - time * 3.3 + 1.7) * .4) * swell * min(1.0, level * 8.0);
        float edge = fwidth(vUv.y) * 1.5, inside = smoothstep(surface + edge, surface - edge, vUv.y);
        if (inside <= 0.0) discard;
        float depth = clamp(surface - vUv.y, 0.0, 1.0);
        // Linear colours: old blood (#6e0e08) going black-red (#2a0202) in its depths.
        vec3 colour = mix(vec3(.157, .004, .002), vec3(.023, .0, .0), smoothstep(0.0, .4, depth));
        // A slow shimmer drifting through the blood, and a few bubbles rising from the bottom and bursting at the surface.
        colour += vec3(.03, .002, .001) * (sin(vUv.x * 31.0 + time * .9) * sin(vUv.y * 23.0 - time * 1.4)) * (1.0 - smoothstep(0.0, .35, depth));
        for (int i = 0; i < 4; i++) {
          float seed = float(i) * 1.618, x = fract(seed * 7.13 + .21), speed = .05 + fract(seed * 3.7) * .05;
          float y = fract(time * speed + seed) * surface, r = .012 + fract(seed * 5.3) * .01;
          vec2 d = (vUv - vec2(x + sin(time * 1.3 + seed * 4.0) * .02, y)) * vec2(.636, 1.0);
          colour += vec3(.22, .04, .02) * smoothstep(r, r * .4, length(d)) * smoothstep(0.0, .03, surface - y) * .55;
        }
        // The meniscus: a lit line where the blood meets the air, with a darker skin just under it.
        colour = mix(colour, vec3(.01, .0, .0), smoothstep(.006, .016, depth) * (1.0 - smoothstep(.016, .04, depth)) * .5);
        colour += vec3(.52, .08, .04) * smoothstep(.01, 0.0, depth) * .8;
        colour *= 1.0 - dim * .45;
        gl_FragColor = vec4(colour, inside * (.72 + .24 * smoothstep(0.0, .35, depth)));
        #include <colorspace_fragment>
      }`,
  });
}

/**
 * Temporary hit points as a pale-gold ward hanging down from the top of the portrait like a pane of old glass: `level` is the share
 * it covers (temporary HP over maximum HP). Light catches its lower edge and drifts down it in slow streaks; `shimmer` (0–1) runs a
 * bright ripple through it just after a blow breaks part of it away.
 */
function wardMaterial() {
  return new THREE.ShaderMaterial({
    transparent: true, depthWrite: false,
    uniforms: { level: { value: 0 }, time: { value: 0 }, shimmer: { value: 0 }, dim: { value: 0 } },
    vertexShader: 'varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
    fragmentShader: `
      uniform float level, time, shimmer, dim; varying vec2 vUv;
      void main() {
        if (level <= .001) discard;
        // The lower edge is not quite straight: a faint ripple, livelier while it shimmers.
        float edgeY = 1.0 - level + sin(vUv.x * 11.0 + time * 1.3) * (.003 + shimmer * .012);
        float aa = fwidth(vUv.y) * 1.5, inside = smoothstep(edgeY - aa, edgeY + aa, vUv.y);
        if (inside <= 0.0) discard;
        float below = vUv.y - edgeY;
        // Linear colours: a pale old gold (#d8b870), clearer toward the top, with a bright gilt lip (#f4e0a8) at the edge.
        vec3 colour = mix(vec3(.62, .42, .12), vec3(.8, .62, .26), smoothstep(.25, .0, below));
        float streak = pow(max(0.0, sin(vUv.x * 9.0 + vUv.y * 3.0 - time * .5)), 8.0) * .5 + pow(max(0.0, sin(vUv.x * 23.0 - vUv.y * 5.0 + time * .3 + 1.3)), 12.0) * .3;
        float lip = smoothstep(.022, .0, below);
        float ripple = shimmer * smoothstep(.12, .0, abs(fract(vUv.y * 1.5 - time * 1.4) - .5) - .3);
        colour += vec3(.9, .76, .42) * (lip * .9 + streak * .35 + ripple * .6);
        float alpha = .34 + .14 * smoothstep(.3, .0, below) + lip * .5 + streak * .16 + ripple * .25;
        colour *= 1.0 - dim * .5;
        gl_FragColor = vec4(colour, inside * alpha * (1.0 - dim * .4));
        #include <colorspace_fragment>
      }`,
  });
}

/**
 * One hero's medallion: an upright bronze frame with chamfered corners, the hero's painted portrait (or else the class glyph) set in it,
 * blood rising over the portrait as the hero is hurt, a gold ward hanging over it while the hero holds temporary hit points, the action crystals on a rail under its foot and the wound crystals on one across
 * its top. Units are CSS pixels; the overlay places it.
 */
export class HeroMedallion extends THREE.Group {
  readonly ready: Promise<void>;
  private metal = bronze(23, 0x4f3d28);
  private blood = bloodMaterial();
  private wardGlass = wardMaterial();
  private beads: Pip[] = [];
  /** Crystals: lit from within while they hold, gone dark and smoky when spent, like a lamp blown out. */
  private actionOn = crystal(0x5aa6ff, 0x2a74ff, 1.9);
  private actionOff = crystal(0x34465e, 0x0c1a2c, .35);
  private woundOn = crystal(0xff8a74, 0xd8301c, 1.6);
  /** A spent wound crystal goes dark crimson and smoulders with a slow pulse: danger, not a dead stone. */
  private woundOff = crystal(0x8c0d0b, 0x7a0000, .7);
  private halo: THREE.Sprite;
  /** A gilt rim round a selected or hovered medallion, lit from within. */
  private crown = new THREE.Mesh(rectFrameGeometry(OUTER_WIDTH + 12, OUTER_HEIGHT + 12, OUTER_WIDTH + 6, OUTER_HEIGHT + 6, 1.5, 7, .6, 1), new THREE.MeshStandardMaterial({ color: 0xe8c885, metalness: .6, roughness: .25, emissive: 0xe8b060, emissiveIntensity: 0 }));
  /** The frame's facets: the same bronze, flat-shaded so each bevel takes the light as one plane. */
  private facets = Object.assign(bronze(29, 0x4f3d28), { flatShading: true });
  private chosen = 0;
  private face?: GlyphFace;
  /** What the blood shows now, easing toward `hp`, and how hard it is sloshing (0–1) after a change. */
  private shown = -1;
  private slosh = 0;
  /** What the ward shows now, easing toward `ward`, and its shimmer after a blow breaks some away (0–1). */
  private wardShown = -1;
  private shimmer = 0;
  /** Temporary hit points as a fraction of the maximum; the ward covers that share of the portrait. */
  ward = 0;
  hp = 1; actions = 2; slots = HERO_ACTIONS; selected = true; hovered = false;
  /** A fallen hero's portrait goes dark and grey. */
  fallen = false;
  /** A hero whose player is away dims part of the way. */
  away = false;
  private faceMaterial: THREE.MeshStandardMaterial;
  private fade = 0;
  /** Wounds taken and the most the hero can bear; each is a crystal on the rail across the top. */
  wounds = 0; maxWounds = 0;
  /** The summons the hero has out (Shadows) and the most it may have; each is a smoky crystal on a rail down the frame's right side. */
  summons = 0; summonLimit = 0;
  private summonPips: Pip[] = [];
  private summonHolder: THREE.Group | null = null;
  private summonOn = crystal(0xb7a6e0, 0x6a48b8, 1.5);
  private summonOff = crystal(0x2c2638, 0x0d0915, .3);
  /**
   * The dice a hero holds and how many it may hold, down the frame's right side, each bearing its rolled value: a Berserker's Fury Dice
   * (blood-red d4 crystals) or an Oathsworn's Judgment Dice (golden d6 cubes).
   */
  fury: readonly number[] = []; furyLimit = 0; diceKind: 'fury' | 'judgment' = 'fury';
  private builtKind: 'fury' | 'judgment' = 'fury';
  /** Raging: the halo burns red and throbs, whether or not the hero is chosen. */
  raging = false;
  private rage = 0;
  private furyPips: Pip[] = [];
  private furyNumbers: THREE.Sprite[] = [];
  private furyHolder: THREE.Group | null = null;
  private furyOn = crystal(0xff5a3a, 0xc81a08, 1.8);
  private furyOff = crystal(0x3a1512, 0x120302, .3);
  private judgmentOn = crystal(0xffe7a0, 0xd89a18, 1.8);
  private judgmentOff = crystal(0x352b14, 0x100b02, .3);
  private cube = new THREE.BoxGeometry(9.5, 9.5, 9.5).rotateX(Math.PI / 4).rotateZ(Math.atan(Math.SQRT1_2));
  private tetra = new THREE.TetrahedronGeometry(8.5, 0).rotateX(-Math.atan(Math.SQRT1_2)).rotateY(Math.PI / 4);
  /** The stances the hero holds, each a small glyph notch that slides out of the frame's left side while it holds. */
  stances: MedallionStances = { move: null, anticipating: false, hidden: false };
  private stanceNotches: Notch[];
  private notchRim = polygonFrameGeometry(8, NOTCH, NOTCH * .74, 2, .8, 1);
  private notchFace = polygonGeometry(8, NOTCH * .76);
  private notchBack = new THREE.CylinderGeometry(NOTCH * .98, NOTCH * 1.06, 6, 8, 1, false, Math.PI / 8).rotateX(Math.PI / 2);
  private gem = new THREE.OctahedronGeometry(7, 0).scale(.7, 1.15, .6);
  private iron = iron();
  private notches: Pip[] = [];
  private woundHolder: THREE.Group | null = null;
  /** Action crystals are cut diamonds; wound crystals are shards set point-down, like notches cut into the frame. */
  private diamond = new THREE.OctahedronGeometry(11, 0).scale(.72, 1.3, .62);
  private shard = new THREE.ConeGeometry(5.5, 15, 4, 1);
  private glow: THREE.Texture | null;
  /** Overall scale: your heroes' medallions are full size, other players' smaller. */
  size = 1;
  private hover = 0;

  /** `portrait`, when given, fills the frame in place of the glyph. */
  constructor(icon: string, tint: number, glow: THREE.Texture | null, private portrait: THREE.Texture | null = null) {
    super();
    this.glow = glow;
    // A heavy outer frame and a thin inner lip round the portrait, and a plate behind. One mesh for both: they share the facets and never move apart.
    const frames = new THREE.Mesh(mergedGeometry([rectFrameGeometry(OUTER_WIDTH, OUTER_HEIGHT, PORTRAIT_WIDTH + 5, PORTRAIT_HEIGHT + 5, 5, 8, 1.8, 1),
      rectFrameGeometry(PORTRAIT_WIDTH + 5, PORTRAIT_HEIGHT + 5, PORTRAIT_WIDTH, PORTRAIT_HEIGHT, 3, 4, 1, 1)].map(geometry => [geometry, new THREE.Matrix4()])), this.facets);
    frames.position.z = -3;
    const backing = new THREE.Mesh(rectFrameGeometry(OUTER_WIDTH + 8, OUTER_HEIGHT + 8, 0, 0, 4, 9, 2, 1), this.metal); backing.position.z = -12;
    this.halo = new THREE.Sprite(new THREE.SpriteMaterial({ map: glow, color: 0xc98a44, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true, opacity: .35 }));
    unpickable(this.halo); this.halo.scale.set(200, 270, 1); this.halo.position.z = -30;
    const shadow = new THREE.Sprite(new THREE.SpriteMaterial({ map: glow, color: 0x000000, transparent: true, opacity: .75, depthWrite: false }));
    unpickable(shadow); shadow.scale.set(160, 230, 1); shadow.position.set(6, -10, -40);
    // A hex bolt in each corner holds the frame to its backing.
    const bx = OUTER_WIDTH / 2 - 4.5, by = OUTER_HEIGHT / 2 - 4.5;
    this.add(new THREE.Mesh(copiesGeometry(boltGeometry(3), [[bx, by], [-bx, by], [bx, -by], [-bx, -by]].map(([x, y]) => new THREE.Vector3(x, y, 5))), this.iron));
    // The blood stands just in front of the portrait, inside the lip.
    const blood = unpickable(new THREE.Mesh(new THREE.PlaneGeometry(PORTRAIT_WIDTH, PORTRAIT_HEIGHT), this.blood)); blood.position.z = -1.4;
    // The ward hangs in front of the blood.
    const ward = unpickable(new THREE.Mesh(new THREE.PlaneGeometry(PORTRAIT_WIDTH, PORTRAIT_HEIGHT), this.wardGlass)); ward.position.z = -1.2;
    this.add(shadow, this.halo, backing, frames, blood, ward, this.crown);
    this.beads = this.holder(3, -RAIL, ACTION_STEP, this.diamond, 0x3a8cff).pips;
    this.stanceNotches = [NOTCH_MOVE_Y, NOTCH_GUARD_Y].map(y => this.notch(y));
    // A portrait carries its own light (it was painted lit): it shows nearly as painted, and the lamps only glaze it like varnish.
    const faceMaterial = this.faceMaterial = portrait
      ? new THREE.MeshStandardMaterial({ map: portrait, color: new THREE.Color().setScalar(PORTRAIT_LIT), emissiveMap: portrait, emissive: 0xffffff, emissiveIntensity: PORTRAIT_GLOW, metalness: 0, roughness: .35 })
      : new THREE.MeshStandardMaterial({ color: 0x1a120b, metalness: .45, roughness: .55, map: blankFace().map, normalMap: blankFace().normalMap });
    const face = new THREE.Mesh(new THREE.PlaneGeometry(PORTRAIT_WIDTH, PORTRAIT_HEIGHT), faceMaterial); face.position.z = -2; this.add(face);
    this.ready = portrait ? Promise.resolve() : glyphFace(icon, 0x1a120b, tint, true, .14).then(glyph => {
      if (this.gone) { glyph.dispose(); return; }
      // The square glyph face stands in the middle of the tall frame, its dark field running on above and below.
      for (const texture of [glyph.map, glyph.normalMap]) { texture.repeat.set(1, PORTRAIT_HEIGHT / PORTRAIT_WIDTH); texture.offset.set(0, (1 - texture.repeat.y) / 2); }
      this.face = glyph; faceMaterial.map = glyph.map; faceMaterial.normalMap = glyph.normalMap; faceMaterial.color.setHex(0xffffff);
    });
  }

  /** Where the `index`th of `count` action crystals stands, in the medallion's own units. */
  actionPoint(index: number, count: number) { return new THREE.Vector2((index - (count - 1) / 2) * ACTION_STEP, -RAIL); }
  /** Where the `index`th of `count` wound crystals stands, in the medallion's own units. */
  woundPoint(index: number, count: number) { return new THREE.Vector2((index - (count - 1) / 2) * WOUND_STEP, RAIL); }
  /** A point on the frame's outer edge, `t` (0–1) of the way round it from the top right corner. */
  rimPoint(t: number) {
    const w = OUTER_WIDTH, h = OUTER_HEIGHT, along = (t % 1) * 2 * (w + h);
    if (along < h) return new THREE.Vector2(w / 2, h / 2 - along);
    if (along < h + w) return new THREE.Vector2(w / 2 - (along - h), -h / 2);
    if (along < 2 * h + w) return new THREE.Vector2(-w / 2, -h / 2 + (along - h - w));
    return new THREE.Vector2(-w / 2 + (along - 2 * h - w), h / 2);
  }
  /** The portrait's half size, in the medallion's own units. */
  static readonly half = new THREE.Vector2(PORTRAIT_WIDTH / 2, PORTRAIT_HEIGHT / 2);

  /** A short bronze rail across the frame at height `y`, with `count` crystals set in it `step` apart, pointing up and down. */
  private holder(count: number, y: number, step: number, geometry: THREE.BufferGeometry, glowColor: number) {
    const group = new THREE.Group(), span = Math.max(1, count - 1) * step + step * 1.3;
    // A broad bronze bar with a dark seat in front of it for the crystals.
    const band = new THREE.Mesh(rectFrameGeometry(span + 8, 17, 0, 0, 4, 6, 1.5, 1), this.facets); band.position.set(0, y, -6);
    const lip = new THREE.Mesh(rectFrameGeometry(span, 10, 0, 0, 1, 4, .5, 1), this.hollow); lip.position.set(0, y, -.5);
    group.add(band, lip);
    // The glows stand in front of the crystals; as points they cost one draw for the whole holder rather than one each.
    const positions = new Float32Array(count * 3), colors = new Float32Array(count * 3);
    const glows: HolderGlows | null = this.glow ? { points: unpickable(new THREE.Points(new THREE.BufferGeometry(), new THREE.PointsMaterial({ map: this.glow, size: GLOW, sizeAttenuation: false, vertexColors: true, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true }))), colors, color: new THREE.Color(glowColor) } : null;
    const pips: Pip[] = Array.from({ length: count }, (_, k) => {
      const mesh = new THREE.Mesh(geometry, this.actionOff);
      // Diamonds stand upright; shards (cones) point down, toward the portrait.
      mesh.position.set((k - (count - 1) / 2) * step, y, 8); mesh.rotation.z = geometry === this.shard ? Math.PI : 0;
      positions.set([mesh.position.x, mesh.position.y, 20], k * 3);
      group.add(mesh); return { mesh, glows, index: k, light: 0 };
    });
    if (glows) {
      glows.points.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      glows.points.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3).setUsage(THREE.DynamicDrawUsage));
      group.add(glows.points); this.glowSets.push(glows);
    }
    this.add(group);
    return { group, pips };
  }
  private hollow = new THREE.MeshStandardMaterial({ color: 0x0c0907, roughness: .9 });

  /**
   * A small octagonal plate on the frame's left edge at height `y`: a bronze rim round an iron cup, the stance's glyph raised from its
   * dark face, a glow behind. It sits out of the column's measure, so a stance coming and going never shifts the column.
   */
  private notch(y: number): Notch {
    const group = new THREE.Group(); group.visible = false; group.userData.unmeasured = true; group.userData.stanceNotch = true;
    const back = new THREE.Mesh(this.notchBack, this.iron); back.position.z = -3;
    const rim = new THREE.Mesh(this.notchRim, this.facets); rim.position.z = .5;
    const face = new THREE.MeshStandardMaterial({ color: 0xffffff, metalness: .2, roughness: .38, emissive: 0x000000, emissiveIntensity: 1, map: blankFace().map, normalMap: blankFace().normalMap, emissiveMap: blankFace().map });
    const plate = new THREE.Mesh(this.notchFace, face); plate.position.z = 1;
    group.add(back, rim, plate);
    const glow = this.glow ? unpickable(new THREE.Sprite(new THREE.SpriteMaterial({ map: this.glow, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true, opacity: 0 }))) : null;
    if (glow) { glow.scale.setScalar(NOTCH * 4.4); glow.position.z = -6; group.add(glow); }
    group.position.set(-OUTER_WIDTH / 2, y, 4);
    this.add(group);
    return { group, face, glow, y, look: null, light: 0 };
  }
  /** Lights a notch with `look`, or lets it sink back into the frame (null); a new glyph shows once it has been drawn. */
  private showNotch(notch: Notch, look: MedallionStance | 'hidden' | null, dt: number, time: number) {
    if (look && look !== notch.look) {
      notch.look = look;
      const shown = STANCE_LOOKS[look], material = notch.face;
      material.emissive.setHex(shown.emissive);
      notch.group.userData.stance = look === 'hidden' ? 'hide' : look;
      if (canDraw()) void stanceFace(shown).then(face => { if (face && notch.look === look) { material.map = material.emissiveMap = face.map; material.normalMap = face.normalMap; } });
    }
    notch.light += ((look ? 1 : 0) - notch.light) * Math.min(1, dt * 9);
    // It slides out from behind the rim as it lights and back in as it goes.
    notch.group.visible = notch.light > .02;
    notch.group.position.x = -OUTER_WIDTH / 2 + (1 - notch.light) * 8;
    notch.group.scale.setScalar(.55 + notch.light * .45);
    if (notch.glow && notch.look) {
      const shown = STANCE_LOOKS[notch.look], material = notch.glow.material as THREE.SpriteMaterial;
      material.color.setHex(shown.glow);
      material.opacity = notch.light * shown.strength * (1 - this.fade * .8) * (.85 + Math.sin(time * 2.1 + notch.y) * .15);
    }
  }
  /** Every holder's glows, sized each frame to the medallion's scale. */
  private glowSets: HolderGlows[] = [];

  update(dt: number, time: number) {
    const fraction = Math.max(0, Math.min(1, this.hp));
    if (this.shown < 0) this.shown = fraction;
    // A wound pours the blood in quickly and healing drains it slowly; either change sets it sloshing, which settles over a few seconds.
    const change = (fraction - this.shown) * Math.min(1, dt * (fraction < this.shown ? 5 : 2.2));
    this.shown += change; this.slosh = Math.min(1, this.slosh + Math.abs(change) * 9) * Math.max(0, 1 - dt * .9);
    const uniforms = this.blood.uniforms;
    uniforms.level.value = 1 - this.shown; uniforms.time.value = time; uniforms.slosh.value = this.slosh; uniforms.dim.value = this.fade;
    // A blow breaks the ward away at once and sets it shimmering; a new ward settles in over a moment.
    const warded = Math.max(0, Math.min(1, this.ward));
    if (this.wardShown < 0) this.wardShown = warded;
    if (warded < this.wardShown - 1e-3) this.shimmer = 1;
    this.wardShown += (warded - this.wardShown) * Math.min(1, dt * (warded < this.wardShown ? 9 : 3));
    this.shimmer = Math.max(0, this.shimmer - dt * .8);
    const ward = this.wardGlass.uniforms;
    ward.level.value = this.wardShown; ward.time.value = time; ward.shimmer.value = this.shimmer; ward.dim.value = this.fade;
    // Each crystal fades in or out rather than switching, the way a light catches or dies.
    const shine = (pip: Pip, on: boolean, lit: THREE.Material, dark: THREE.Material, pulse: number) => {
      pip.light += ((on ? 1 : 0) - pip.light) * Math.min(1, dt * 8);
      pip.mesh.material = pip.light > .5 ? lit : dark;
      if (pip.glows) { const { color, colors } = pip.glows, strength = pip.light * (.6 + pulse); colors[pip.index * 3] = color.r * strength; colors[pip.index * 3 + 1] = color.g * strength; colors[pip.index * 3 + 2] = color.b * strength; }
    };
    // A hero has two action crystals, a third while steady; the rail grows to take more only while an effect grants extra actions.
    const actionCount = Math.max(this.slots, this.actions);
    if (this.beads.length !== actionCount) {
      const old = this.beads[0]?.mesh.parent;
      if (old) { old.removeFromParent(); this.release(old); }
      this.beads = this.holder(actionCount, -RAIL, ACTION_STEP, this.diamond, 0x3a8cff).pips;
    }
    this.beads.forEach((pip, k) => shine(pip, k < this.actions, this.actionOn, this.actionOff, Math.sin(time * 2.6 + k) * .08));
    // One wound crystal per wound the hero can bear, on a rail across the top: each burns red while it is spared, and goes out
    // as a wound is taken, from the right, so the lit ones count what the hero can still bear.
    if (this.notches.length !== this.maxWounds) {
      if (this.woundHolder) { this.woundHolder.removeFromParent(); this.release(this.woundHolder); }
      const holder = this.maxWounds ? this.holder(this.maxWounds, RAIL, WOUND_STEP, this.shard, 0xd8301c) : null;
      this.woundHolder = holder?.group ?? null; this.notches = holder?.pips ?? [];
    }
    // One crystal per summon the hero may keep, on a rail stood upright against the frame's right side; each burns violet while its summon lasts.
    if (this.summonPips.length !== this.summonLimit) {
      if (this.summonHolder) { this.summonHolder.removeFromParent(); this.release(this.summonHolder); }
      const holder = this.summonLimit ? this.holder(this.summonLimit, 0, SUMMON_STEP, this.gem, 0x7a5ad0) : null;
      if (holder) { holder.group.rotation.z = Math.PI / 2; holder.group.position.set(OUTER_WIDTH / 2 + 6, 0, 2); }
      this.summonHolder = holder?.group ?? null; this.summonPips = holder?.pips ?? [];
    }
    this.summonPips.forEach((pip, k) => shine(pip, k < this.summons, this.summonOn, this.summonOff, Math.sin(time * 2.2 + k * 1.7) * .12));
    // One crystal per die the hero may hold, on the same upright rail; a held die burns (blood red, or gold for Judgment) and shows its value.
    const judgment = this.diceKind === 'judgment';
    if (this.furyPips.length !== this.furyLimit || this.builtKind !== this.diceKind) {
      if (this.furyHolder) { this.furyHolder.removeFromParent(); this.release(this.furyHolder); }
      this.builtKind = this.diceKind;
      const holder = this.furyLimit ? this.holder(this.furyLimit, 0, FURY_STEP, judgment ? this.cube : this.tetra, judgment ? 0xffc23a : 0xff3018) : null;
      if (holder) { holder.group.rotation.z = Math.PI / 2; holder.group.position.set(OUTER_WIDTH / 2 + 6, 0, 2); }
      this.furyHolder = holder?.group ?? null; this.furyPips = holder?.pips ?? [];
      this.furyNumbers = this.furyPips.map(pip => {
        const sprite = unpickable(new THREE.Sprite(new THREE.SpriteMaterial({ transparent: true, depthWrite: false, depthTest: false, opacity: 0 })));
        sprite.scale.setScalar(17); sprite.position.set(pip.mesh.position.x, pip.mesh.position.y, 16); holder!.group.add(sprite); return sprite;
      });
    }
    this.furyPips.forEach((pip, k) => {
      const value = this.fury[k];
      pip.mesh.rotation.y = time * .9 + k * 2.1;
      shine(pip, value !== undefined, judgment ? this.judgmentOn : this.furyOn, judgment ? this.judgmentOff : this.furyOff, Math.sin(time * 4.2 + k * 1.3) * .18 + this.rage * .25);
      const label = this.furyNumbers[k], material = label.material as THREE.SpriteMaterial, texture = value === undefined ? null : furyNumber(value);
      if (texture && material.map !== texture) { material.map = texture; material.needsUpdate = true; }
      material.opacity = pip.light * (1 - this.fade * .7);
    });
    this.woundOff.emissiveIntensity = .55 + Math.sin(time * 3.4) * .3;
    this.notches.forEach((pip, k) => shine(pip, k < this.maxWounds - this.wounds, this.woundOn, this.woundOff, Math.sin(time * 3 + k) * .1));

    const { move, anticipating, hidden } = this.stances;
    this.showNotch(this.stanceNotches[0], move === 'hide' && hidden ? 'hidden' : move, dt, time);
    this.showNotch(this.stanceNotches[1], anticipating ? 'anticipate-danger' : null, dt, time);
    this.fade += ((this.fallen ? 1 : this.away ? .6 : 0) - this.fade) * Math.min(1, dt * 3);
    if (this.portrait) {
      this.faceMaterial.color.setRGB(1 - this.fade * .45, 1 - this.fade * .55, 1 - this.fade * .5).multiplyScalar(PORTRAIT_LIT); this.faceMaterial.emissiveIntensity = PORTRAIT_GLOW * (1 - this.fade * .75);
    }
    this.hover += ((this.hovered ? 1 : 0) - this.hover) * Math.min(1, dt * 12);
    // Selected medallions stand forward, bright and rimmed in gilt; the others sink back, dimmer.
    this.chosen += ((this.selected ? 1 : 0) - this.chosen) * Math.min(1, dt * 10);
    // Hover warms the rim more quietly than selection, without changing which hero is chosen.
    const lit = Math.max(this.chosen, this.hover * .6);
    // A Rage turns the halo to blood and sets it throbbing like a pulse, chosen or not.
    this.rage += ((this.raging && !this.fallen ? 1 : 0) - this.rage) * Math.min(1, dt * 4);
    const throb = Math.pow(Math.max(0, Math.sin(time * 7.5)), 6) * .35;
    this.halo.visible = lit > .02 || this.rage > .02;
    (this.halo.material as THREE.SpriteMaterial).color.copy(HALO).lerp(RAGE_HALO, this.rage);
    (this.halo.material as THREE.SpriteMaterial).opacity = Math.max((lit + this.hover * .3) * (.55 + Math.sin(time * 7.3) * .04 + Math.sin(time * 3.1) * .05), this.rage * (.4 + throb * .5));
    this.metal.color.copy(RESTING_BRONZE).lerp(SELECTED_BRONZE, lit); this.facets.color.copy(this.metal.color);
    this.crown.visible = lit > .02; this.crown.scale.setScalar(.97 + lit * .03);
    (this.crown.material as THREE.MeshStandardMaterial).emissiveIntensity = (lit + this.hover * .45) * (.8 + Math.sin(time * 2.4) * .15);
    this.scale.setScalar(this.size * (1 + this.hover * .04 + this.chosen * .06));
    this.position.z = this.chosen * 20;
    // Points are sized in screen pixels: as wide as the glow sprites they replace at this scale. A holder with every glow out is not drawn.
    for (const { points, colors } of this.glowSets) {
      points.material.size = GLOW * this.scale.x;
      points.visible = colors.some(value => value > 1 / 1024);
      if (points.visible) points.geometry.attributes.color.needsUpdate = true;
    }
  }
  /** Frees a holder taken off the frame: its own rails and glows (the crystal geometries are shared). */
  private release(holder: THREE.Object3D) {
    holder.traverse(object => {
      if (object instanceof THREE.Mesh && object.geometry !== this.diamond && object.geometry !== this.shard) object.geometry.dispose();
      if (object instanceof THREE.Points) { object.geometry.dispose(); (object.material as THREE.Material).dispose(); this.glowSets = this.glowSets.filter(set => set.points !== object); }
    });
  }

  /** Set once disposed, so a glyph still being drawn is freed when it arrives rather than kept. The portrait texture is its owner's. */
  private gone = false;
  dispose() {
    this.gone = true;
    this.traverse(object => { if (object instanceof THREE.Mesh || object instanceof THREE.Points) object.geometry.dispose(); if (object instanceof THREE.Sprite || object instanceof THREE.Points) object.material.dispose(); });
    for (const notch of this.stanceNotches) notch.face.dispose();
    this.face?.dispose(); this.faceMaterial.dispose(); this.blood.dispose(); this.wardGlass.dispose(); this.facets.dispose(); this.iron.dispose(); this.hollow.dispose(); this.diamond.dispose(); this.shard.dispose(); for (const material of [this.actionOn, this.actionOff, this.woundOn, this.woundOff]) material.dispose(); (this.crown.material as THREE.Material).dispose(); this.metal.dispose();
  }
}

/**
 * The HUD's lit scene, drawn after the world into the same renderer: a warm flickering lamp and a cool sky fill, like the sealed
 * door. It is laid out in CSS pixels from the bottom left, so it follows the canvas size directly. Pointing is a raycast, nothing more.
 */
export class HudOverlay {
  readonly scene = new THREE.Scene();
  // The camera stands in front of every piece, so a picking ray starts before anything raised toward the viewer.
  readonly camera = (() => { const camera = new THREE.OrthographicCamera(0, 1, 1, 0, 1, 1000); camera.position.z = 500; return camera; })();
  readonly medallion: HeroMedallion;
  readonly menu: RadialMenu;
  private lamp = new THREE.PointLight(0xffb469, 2.4, 0, 0);
  private menuLamp = new THREE.PointLight(0xffb469, 1.8, 0, 0);
  /** The selected hero on screen, in CSS pixels from the canvas's top left; null hides the menu. */
  private anchor: { x: number; y: number } | null = null;
  private glow = canDraw() ? glowTexture() : null;
  private raycaster = new THREE.Raycaster();
  private pointer: { x: number; y: number } | null = null;
  private size = { width: 1, height: 1 };
  private time = 0;

  constructor(icon: string, tint: number, abilities: RadialNode[]) {
    this.medallion = new HeroMedallion(icon, tint, this.glow);
    this.menu = new RadialMenu(abilities, this.glow);
    this.scene.add(this.medallion, this.menu, this.lamp, this.menuLamp, new THREE.HemisphereLight(0x8aa0bf, 0x1a120a, 1.1), new THREE.AmbientLight(0x30261c, .6));
  }

  /** Places the menu around the selected hero's point on screen, or hides it. */
  setAnchor(anchor: { x: number; y: number } | null) { this.anchor = anchor; }

  /** The pointer in CSS pixels from the canvas's top left, or null when it left. Returns what it is over: `medallion`, a socket id, or null. */
  point(position: { x: number; y: number } | null): string | null {
    this.pointer = position;
    this.medallion.hovered = false; this.menu.hover(null);
    if (!position) return null;
    this.raycaster.setFromCamera(new THREE.Vector2(position.x / this.size.width * 2 - 1, -(position.y / this.size.height) * 2 + 1), this.camera);
    const socket = this.menu.visible ? this.menu.pick(this.raycaster) : null;
    if (socket) { this.menu.hover(socket); return socket; }
    this.medallion.hovered = this.raycaster.intersectObject(this.medallion, true).some(hit => hit.object instanceof THREE.Mesh);
    return this.medallion.hovered ? 'medallion' : null;
  }

  update(dt: number) {
    this.time += dt;
    const { medallion } = this, { width, height } = this.size;
    medallion.position.set(92, 120, 0);
    // The lamp hangs up and to the left, and flickers like a flame; the medallion leans a touch toward the pointer.
    this.lamp.position.set(medallion.position.x - 70, medallion.position.y + 110, 150);
    this.lamp.intensity = 2.4 * (1 + Math.sin(this.time * 11.3) * .04 + Math.sin(this.time * 5.7) * .05);
    const lean = this.pointer ? { x: (this.pointer.x - medallion.position.x) / width, y: (height - this.pointer.y - medallion.position.y) / height } : { x: 0, y: 0 };
    medallion.rotation.y += (lean.x * .5 - medallion.rotation.y) * Math.min(1, dt * 5);
    medallion.rotation.x += (-lean.y * .5 - medallion.rotation.x) * Math.min(1, dt * 5);
    medallion.update(dt, this.time);
    this.menu.visible = !!this.anchor && medallion.selected;
    if (this.anchor) {
      this.menu.position.set(this.anchor.x, height - this.anchor.y, 0);
      this.menuLamp.position.set(this.menu.position.x - 90, this.menu.position.y + 130, 180);
    }
    this.menuLamp.intensity = this.menu.visible ? this.lamp.intensity * .8 : 0;
    this.menu.update(dt);
  }

  /** Draws over whatever the renderer drew last, keeping its colour and clearing only depth. */
  render(renderer: THREE.WebGLRenderer, width: number, height: number) {
    if (width !== this.size.width || height !== this.size.height) {
      this.size = { width, height };
      Object.assign(this.camera, { left: 0, right: width, top: height, bottom: 0 }); this.camera.updateProjectionMatrix();
    }
    const autoClear = renderer.autoClear, shadows = renderer.shadowMap.autoUpdate;
    renderer.autoClear = false; renderer.shadowMap.autoUpdate = false;
    renderer.clearDepth(); renderer.render(this.scene, this.camera);
    renderer.autoClear = autoClear; renderer.shadowMap.autoUpdate = shadows;
  }

  dispose() { this.medallion.dispose(); this.menu.dispose(); this.glow?.dispose(); }
}
